import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	List,
	ListItem,
	ListItemText,
	LinearProgress,
	CardContent,
	Card,
	Button,
	Chip,
	CircularProgress,
	Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { updateFormStatus } from "../../apiServices/services/ProfileServices";
import { getCompanyData } from "../../apiServices/services/DocumentsServices";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BusinessBanking from "../BusinessBanking/BusinessBanking";
import Insurances from "../Insurance/Insurance";
import WebDomain from "../WebDomain/WebDomain";
import AccountSoftware from "../AccountingSoftware.js/AccountSoftware";
import WorkEmail from "../WorkEmail/WorkEmail";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import instance from "../../apiServices/instance";
import socketService from "../../services/socketService";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import { useToast } from "../../hooks/useToast";

const statusDetails = {
	pending: { label: "Pending", color: "warning" },
	accept: { label: "Accepted", color: "success" },
	reject: { label: "Rejected", color: "error" },
};

const StatusEnum = {
	not_initiated: "not_initiated",
	pending: "pending",
	accept: "accept",
	reject: "reject",
};

const DashboardProgressChecklist = () => {
	const companyName = localStorage.getItem("companyName");
	const steps = [
		{
			label: "Incorporate your company",
			completed: true,
			form: "isCompanyIncorporated",
		},
		{
			label: "Set up your Business Bank Account",
			completed: false,
			form: "isBussinesBankSetup",
		},
		{
			label: "Protect yourself with Business Insurance",
			completed: false,
			form: "isBussinessInsuranceCompliant",
		},
		{
			label: "Secure your Website Domain",
			completed: false,
			form: "isWebsiteDomainSecure",
		},
		{
			label: "Stay ontop of your Finances",
			completed: false,
			form: "isFinanceDone",
		},
		{
			label: "Be professional with a work email ",
			completed: false,
			form: "isBusinessConsultationDone",
		},
	];

	const [openTab, setOpenTab] = useState(null);
	const [stepState, setStepState] = useState(steps);
	const [status, setStatus] = useState(StatusEnum.not_initiated);
	const [loading, setLoading] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [isCompanyIncorporatedManually, setIsCompanyIncorporatedManually] =
		useState(false);

	const { showError, showSuccess, showInfo, ToastContainer } = useToast();

	const handleRefresh = async () => {
		setLoading(true);
		try {
			const { data } = await instance.post(
				"/api/company-house/get-submission-status"
			);
			if (data.transactionId) {
				setStatus(StatusEnum[data.status] || StatusEnum.pending);
				showInfo("Status Refreshed SuccessFully", 5000);
			}
		} catch (error) {
			console.log("Error: ", error);
		} finally {
			setLoading(false);
		}
	};

	const progress =
		(stepState.filter((step) => step.completed).length / stepState.length) *
		100;

	const handleToggle = (index) => {
		setOpenTab(openTab === index ? null : index);
	};

	const getCompanyInfo = async (userId) => {
		return await getCompanyData(userId);
	};

	const handleIncorporationUpdate = async (data) => {
		const { initiateCompanyIncorporation } = data?.data;
		if (initiateCompanyIncorporation.isSuccess) {
			setStatus(
				StatusEnum[initiateCompanyIncorporation.companyHouse.status] ||
					StatusEnum.not_initiated
			);
			showSuccess("Company Incorporation request Initiated", 5000);
		} else {
			showError("Failed to initiate incorporation request.", 5000);
		}
	};

	// Fix the useEffect
	useEffect(() => {
		const fetchData = async () => {
			try {
				const userId = localStorage.getItem("userId");
				const response = await getCompanyInfo(userId);
				if (response) {
					// Assuming API returns data in response.data
					const updatedSteps = stepState.map((step) => ({
						...step,
						completed: response[step.form] || false,
					}));

					setStatus(
						response?.company_house?.status ||
							StatusEnum.not_initiated
					);
					setStepState(updatedSteps);

					setIsCompanyIncorporatedManually(
						response?.company.isCompanyIncorporatedManually || false
					);

					// Get companyId from response
					const companyId = response?.company?.id;
					localStorage.setItem("companyId", companyId);
					if (companyId) {
						socketService.subscribeToVerification(companyId);
					} else {
						socketService.connect();
					}
					// const emailRequestBody = {
					//   id: response?.id,
					//   name: response?.firstName,
					//   email: response?.email,
					// };
					// await instance.post(
					//   "/api/send-incorporation-request",
					//   emailRequestBody
					// );
				}
			} catch (error) {
				console.error("Failed to fetch company data:", error);
			}
		};
		fetchData();
		socketService.onIncorporationUpdate(handleIncorporationUpdate);

		return () => {
			const companyId = localStorage.getItem("companyId");
			if (companyId) {
				socketService.unsubscribeFromVerification(companyId);
			}
			socketService.removeAllListeners();
		};
	}, []);

	const handleChangeStatus = async (index, step) => {
		const updatedSteps = stepState.map((item, idx) =>
			idx === index ? { ...item, completed: !item.completed } : item
		);
		const formName = stepState[index].form;

		try {
			const response = await updateFormStatus(
				localStorage.getItem("userId"),
				{
					[formName]: true,
				}
			);

			if (response.status === 200) {
				setStepState(updatedSteps);
			} else {
				console.error("Failed to update form status:", response.status);
			}
		} catch (error) {
			console.error("Error updating form status:", error);
		}
	};

	const getStatusIcon = () => {
		switch (status) {
			case "completed":
				return <CheckCircleIcon sx={{ fontSize: 20, mr: 1 }} />;
			case "failed":
				return <ErrorIcon sx={{ fontSize: 20, mr: 1 }} />;
			default:
				return <HourglassEmptyIcon sx={{ fontSize: 20, mr: 1 }} />;
		}
	};

	return (
		<Box
			sx={{
				overflow: "auto",
			}}>
			<ToastContainer />
			{status !== StatusEnum.accept && !isCompanyIncorporatedManually ? (
				<Box sx={{ px: 6, py: 4 }}>
					{/* Header */}
					<Typography
						variant='h4'
						sx={{ fontWeight: 700, color: "#1a1a1a", mb: 5 }}>
						Company Incorporation
					</Typography>

					{/* Progress Message */}
					<Box sx={{ mb: 5 }}>
						<Typography
							variant='h6'
							sx={{ fontWeight: 600, color: "#2c3e50", mb: 1 }}>
							Your Incorporation Journey Has Started
						</Typography>
						<Typography
							sx={{
								color: "#5a6c7d",
								fontSize: "1rem",
								lineHeight: 1.6,
								maxWidth: 700,
							}}>
							We're processing your incorporation and will notify
							you via email once it's complete. Stay tuned!
						</Typography>
					</Box>

					<Divider sx={{ mb: 4 }} />

					{/* Status Section */}
					<Box sx={{ mb: 5 }}>
						<Typography
							variant='h6'
							sx={{ fontWeight: 600, mb: 2, color: "#2c3e50" }}>
							Incorporation Status
						</Typography>

						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 2,
								py: 2,
								px: 3,
								backgroundColor: "#f9fafb",
								border: "1px solid #e0e0e0",
								borderRadius: 1,
							}}>
							<Chip
								icon={getStatusIcon()}
								label={
									status === "not_initiated"
										? "Not Initiated"
										: statusDetails[status]?.label
								}
								color={
									status === "not_initiated"
										? "default"
										: statusDetails[status]?.color
								}
								variant='filled'
								sx={{
									fontWeight: 600,
									fontSize: "1rem",
									height: 40,
									px: 2,
								}}
							/>

							{status !== "not_initiated" && (
								<Box sx={{ textAlign: "right" }}>
									<Typography
										variant='caption'
										sx={{
											color: "#6c757d",
											mb: 1,
											display: "block",
										}}>
										Not seeing the latest update? Try
										refreshing.
									</Typography>
									<Button
										variant='contained'
										color='primary'
										onClick={handleRefresh}
										startIcon={
											loading ? (
												<CircularProgress
													color='inherit'
													size={18}
												/>
											) : (
												<RefreshIcon />
											)
										}
										disabled={loading}
										sx={{
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 600,
											px: 3,
											py: 1,
											boxShadow: "none",
											"&:hover": {
												boxShadow:
													"0 3px 6px rgba(0,0,0,0.1)",
											},
										}}>
										{loading
											? "Refreshing..."
											: "Refresh Status"}
									</Button>
								</Box>
							)}
						</Box>
					</Box>

					{/* Help Message */}
					<Box sx={{ mt: 4 }}>
						<Typography
							variant='body2'
							sx={{
								color: "#0c5460",
								backgroundColor: "#f0f9ff",
								border: "1px solid #cfe2ff",
								padding: 2.5,
								borderRadius: 1,
								fontSize: "0.95rem",
								lineHeight: 1.6,
								textAlign: "center",
							}}>
							Need assistance? Our incorporation team is here to
							help. The process typically takes 1–3 business days.
							You’ll receive updates along the way.
						</Typography>
					</Box>
				</Box>
			) : (
				<Box>
					<Box p={4} m={3} mt={0.5}>
						<Typography
							variant='h5'
							gutterBottom
							sx={{ fontWeight: "800", color: "black" }}>
							Company Setup
						</Typography>
						<Typography sx={{ color: "black", fontWeight: 450 }}>
							Congratulations, you have now incorporated{" "}
							{companyName}! <br />
							<br />
							Complete the steps below to make sure your business
							is set up for success.
						</Typography>

						<Box
							sx={{
								width: "100%",
								maxWidth: "1200px",
								paddingTop: "1rem",
								paddingBottom: "1rem",
								mt: "1rem",
								minHeight: "auto",
								borderRadius: "10px",
							}}>
							<Typography
								variant='h6'
								gutterBottom
								sx={{ fontWeight: "bold" }}>
								<AutoAwesomeRoundedIcon
									sx={{ color: "blue", mr: 1 }}
								/>
								Your company set up progress
							</Typography>

							{/* Progress Bar with Percentage on the Side */}
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									mt: 2,
								}}>
								<LinearProgress
									variant='determinate'
									value={progress}
									sx={{
										flexGrow: 1,
										height: "1.6rem",
										borderRadius: "3px",
										backgroundColor: "white",
										"& .MuiLinearProgress-bar": {
											backgroundColor: "#4f07ea",
										},
									}}
								/>
								<Typography
									variant='body2'
									sx={{
										ml: 2,
										minWidth: "40px",
										color: "black",
										fontWeight: "bold",
									}} // Add spacing
								>
									{Math.round(progress)}%
								</Typography>
							</Box>
						</Box>

						<Box
							sx={{
								width: "100%",
								maxWidth: "1200px",
								paddingBottom: "1rem",
								minHeight: "auto", // Allow content to grow
								borderRadius: "10px",
							}}>
							<List>
								{stepState.map((step, index) => (
									<Card
										key={index}
										sx={{
											marginBottom: "1rem",
											borderRadius: "10px",
										}}>
										<CardContent>
											<ListItem>
												<Box
													sx={{
														display: "flex",
														flexDirection: "column",
														width: "100%",
													}}>
													<Box
														sx={{
															display: "flex",
															justifyContent:
																"space-between",
															width: "100%",
															fontWeight: "bold",
															// height: "5vh",
															alignItems:
																"center",
														}}
														disableGutters>
														<div className='items-center justify-center mr-6'>
															<span
																style={{
																	color: "rgb(120 69 239)",
																	fontWeight:
																		"500",
																}}>
																{`Step ${
																	index + 1
																}`}
															</span>
														</div>
														<ListItemText
															primary={
																<span
																	style={{
																		display:
																			"flex",
																		alignItems:
																			"center",
																	}}>
																	{step.label}

																	<KeyboardArrowDownOutlinedIcon
																		sx={{
																			fontSize: 35,
																			color: "rgb(120 69 239)",
																			marginLeft:
																				"0.5rem",
																		}}
																	/>
																</span>
															}
															primaryTypographyProps={{
																sx: {
																	fontWeight:
																		"800",
																	color: "black",
																	fontSize:
																		"1.1rem",
																	cursor: "pointer",
																},
															}}
															onClick={() =>
																handleToggle(
																	index
																)
															}
														/>
														{step.completed ? (
															<CheckCircleIcon
																sx={{
																	fontSize: 34,
																	color: "blue",
																}}
																color='primary'
															/>
														) : (
															<RadioButtonUncheckedIcon
																sx={{
																	fontSize: 34,
																	color: "blue",
																	cursor: "pointer",
																}}
																color='action'
																onClick={() => {
																	if (
																		index ===
																		0
																	) {
																		setShowModal(
																			true
																		);
																	} else {
																		handleChangeStatus(
																			index,
																			step
																		);
																	}
																}}
															/>
														)}
													</Box>
													{openTab === index &&
														openTab === 0 && (
															<Box
																sx={{
																	marginLeft:
																		"-1rem",
																}}>
																<Card
																	sx={{
																		borderRadius: 2,
																		border: "1px solid grey",
																		boxShadow:
																			"none",
																		p: 2,
																		margin: "1rem 0",
																		backgroundColor:
																			"#fff",
																	}}>
																	<CardContent>
																		<Typography
																			variant='h6'
																			sx={{
																				mb: 1,
																			}}>
																			Incorporation
																			Status
																		</Typography>

																		<Box
																			sx={{
																				display:
																					"flex",
																				justifyContent:
																					"space-between",
																				alignItems:
																					"center",
																				mb: 2,
																			}}>
																			<Chip
																				label={
																					status ===
																						"not_initiated" ||
																					!statusDetails[
																						status
																					]
																						? "Not Initiated"
																						: statusDetails[
																								status
																						  ]
																								?.label
																				}
																				color={
																					status ===
																						"not_initiated" ||
																					!statusDetails[
																						status
																					]
																						? "default"
																						: statusDetails[
																								status
																						  ]
																								?.color
																				}
																				variant='outlined'
																				sx={{
																					fontWeight:
																						"bold",
																					fontSize:
																						"0.9rem",
																				}}
																			/>
																		</Box>

																		<Typography
																			variant='body2'
																			sx={{
																				color: "text.secondary",
																				lineHeight: 1.6,
																			}}>
																			{status ===
																			"not_initiated" ? (
																				<>
																					To
																					begin
																					incorporating
																					a
																					company,
																					please
																					click
																					the
																					appropriate
																					option
																					above.
																				</>
																			) : (
																				<>
																					We’ve
																					successfully
																					submitted
																					your
																					application
																					to
																					Companies
																					House.
																					Once
																					the
																					company
																					is
																					registered,
																					your
																					incorporation
																					documents
																					will
																					be
																					available
																					in
																					the{" "}
																					<strong>
																						Documents
																					</strong>{" "}
																					section.
																				</>
																			)}
																		</Typography>
																	</CardContent>
																</Card>
															</Box>
														)}
													{openTab === index &&
														openTab === 1 && (
															<Box
																sx={{
																	marginLeft:
																		"-1rem",
																}}>
																<BusinessBanking />
															</Box>
														)}
													{openTab === index &&
														openTab === 2 && (
															<Box
																sx={{
																	marginLeft:
																		"-1rem",
																}}>
																<Insurances />
															</Box>
														)}
													{openTab === index &&
														openTab === 3 && (
															<Box
																sx={{
																	marginLeft:
																		"-1rem",
																}}>
																<WebDomain />
															</Box>
														)}
													{openTab === index &&
														openTab === 4 && (
															<Box
																sx={{
																	marginLeft:
																		"-1rem",
																}}>
																<AccountSoftware />
															</Box>
														)}
													{openTab === index &&
														openTab === 5 && (
															<Box
																sx={{
																	marginLeft:
																		"-1rem",
																}}>
																<WorkEmail />
															</Box>
														)}
												</Box>
											</ListItem>
										</CardContent>
									</Card>
								))}
							</List>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default DashboardProgressChecklist;
